import { Typography } from '@material-ui/core';
import { TEST_ID_CERRAR_MODAL_POLIGONO } from 'components/Maps/OutageDashboard/outageDashboard.constants';

import cerrarModalLogo from 'assets/images/cerrar-modal.svg';
import volverModalLogo from 'assets/images/boton-volver.svg';
import IconButton from '@material-ui/core/IconButton';
import { closePurple } from 'assets/images';
import { InfoIcon, Loading } from '@saesa/ui-kit-front';
import { TIPOCORTE } from 'config/tiposCorte';
import { useTranslation } from 'react-i18next';
import { datoNoValido, esDashboardTrackingMapa } from 'utils/colorBallsUtils';
import { CADENA_VACIA, ID_MODAL_POLIGONO } from 'utils/constants';
import {
  actualizarEstadoCaptchaV2,
  actualizarEstadoCaptchaV3,
  actualizarEstadoMensajeFlotanteCerrado,
  actualizarEstadoModalMobileAbierto,
  actualizarEstadoModalMobileAbiertoCompleto,
} from 'store';
import { useDispatch } from 'react-redux';
import { PATH_MAPA_IFRAME } from 'root-page';
import useStyles from './ModalStyles';

const ModalPoligono = ({
  titulo,
  setTitulo,
  setMostrarModal,
  setMostrarBuscador,
  estadoTracking,
  children,
  dataSinTracking,
  setDataSinTracking,
  setServicioCargado,
  setVentanaMobileState,
  activadorMobile,
  motivoCorte,
  detalleAvanceAbierto,
  compactoDesktop,
  sinGraficoDesktop,
}: {
  titulo?: string;
  setTitulo?: any;
  setMostrarModal: any;
  setMostrarBuscador: any;
  estadoTracking?: any;
  children?: any;
  dataSinTracking?: any;
  setDataSinTracking?: any;
  setServicioCargado?: any;
  setVentanaMobileState?: any;
  activadorMobile?: any;
  motivoCorte?: string;
  /** Vista detalle de avance: modal desktop 744px; mobile sheet con hueco superior ~184px */
  detalleAvanceAbierto?: boolean;
  /** Sin tracking (KML): el modal desktop se ajusta al contenido (64px al footer) */
  compactoDesktop?: boolean;
  /** Desktop sin gráfico de clientes: ancho fijo 760px (Figma programado/imprevisto) */
  sinGraficoDesktop?: boolean;
}) => {
  /**
   * Pagina de estilos de componente.
   */
  const styles = useStyles();
  /**
   * Uso del i18n para traducciones.
   */
  const { t } = useTranslation();

  /**
   * Disparador para el selector
   */
  const dispatch = useDispatch();

  const esDashboardTracking = esDashboardTrackingMapa(titulo) && !dataSinTracking;

  /**
   * Funcion para cerrar ventana modal y abrir buscador
   */
  const cerrarModal = () => {
    setMostrarModal(false);
    setMostrarBuscador(true);
    setServicioCargado(false);
    setTitulo(CADENA_VACIA);
    dispatch(actualizarEstadoCaptchaV3(false));
    dispatch(actualizarEstadoCaptchaV2(false));
    dispatch(actualizarEstadoModalMobileAbierto({ modalMobileAbierto: false }));
    dispatch(actualizarEstadoModalMobileAbiertoCompleto({ modalMobileAbiertoCompleto: false }));
    dispatch(actualizarEstadoMensajeFlotanteCerrado({ mensajeFlotanteCerrado: false }));
    if (dataSinTracking !== CADENA_VACIA) {
      setDataSinTracking(CADENA_VACIA);
    }
  };

  /**
   * Función para obtener la clase del contenedor según ruta, tipo de corte y si el detalle está abierto.
   * @returns clases CSS del modal
   */
  const obtenerClase = () => {
    let base = styles.caja;

    if (window.location.pathname === PATH_MAPA_IFRAME) {
      if (esDashboardTracking) {
        base = `${styles.cajaImprevisto} ${styles.cajaIframe}`;
      } else {
        base = `${styles.caja} ${styles.cajaIframe}`;
      }
    } else if (esDashboardTracking) {
      base = styles.cajaImprevisto;
    }

    if (detalleAvanceAbierto && esDashboardTracking) {
      return `${base} ${styles.cajaImprevistoDetalle}`;
    }

    if (sinGraficoDesktop && esDashboardTracking) {
      const clases = `${base} ${styles.cajaImprevistoSinGrafico}`;
      const ajustado = titulo === TIPOCORTE.PROXIMO_CORTE_PROGRAMADO ? ` ${styles.cajaImprevistoAjustado}` : CADENA_VACIA;
      return compactoDesktop ? `${clases} ${styles.cajaImprevistoCompacto}` : `${clases}${ajustado}`;
    }

    if (compactoDesktop && esDashboardTracking) {
      return `${base} ${styles.cajaImprevistoCompacto}`;
    }

    return base;
  };

  /**
   * Funcion que permite volver a la ventana mobile
   */
  const volverModal = () => {
    setVentanaMobileState(true);
    setMostrarModal(false);
    setServicioCargado(false);
  };

  /**
   * Retorna el estilo del circulo segun sea el tipo de corte.
   *
   * @param tipo de corte
   * @returns Estilo de circulo asociado a corte
   */
  const tipoCorte = (tipo: string) => {
    let corte = CADENA_VACIA;
    switch (tipo) {
      case TIPOCORTE.CORTE_IMPREVISTO:
        corte = styles.circuloRojo;
        break;

      case TIPOCORTE.CORTE_PROGRAMADO:
        corte = styles.circuloNaranja;
        break;
      case TIPOCORTE.PROXIMO_CORTE_PROGRAMADO:
        corte = styles.circuloAzul;
        break;

      default:
        break;
    }
    return corte;
  };

  /**
   * Función que se encarga de dar el estilo correspondiente al boton de cerrar la ventana modal dependiendo el tipo de corte
   * @returns el estilo correspondiente para el tipo de corte
   */
  const obtenerContenedorBotones = () => {
    if (!esDashboardTracking) {
      return styles.contenedorBoton;
    }
    return styles.contenedorBotonImprevistos;
  };

  /**
   * Muestra vista inferior para informar al cliente cuando no hay información de tracking
   * asociado al corte.
   *
   * @returns Retorna vista para cuando no existe información desde tracking
   */
  const mostrarDataSinTracking = () =>
    dataSinTracking?.length > 1 ? (
      <div className={styles.contenedorMensaje}>
        <div className={styles.contenedorTextoIcono}>
          <InfoIcon className={styles.mensajeIcon} />
        </div>

        <div className={styles.parrafo}>
          <Typography className={styles.textoParrafo}>{t('desconexion:paginas.mapaCortes.modalTracking.infoSinTracking')}</Typography>
        </div>
      </div>
    ) : (
      <Loading />
    );

  const contenedoresBotonesModal = () => {
    // Figma mobile imprevisto: X cierra el sheet (no “volver” a card resumen)
    if (activadorMobile && !esDashboardTracking) {
      return (
        <div className={styles.contenedorBotonMobile}>
          <IconButton title="cerrarBotom" aria-label="cerrar" onClick={volverModal} size="small" className={styles.sizeSmall}>
            <img src={volverModalLogo} alt="Cerrar" />
          </IconButton>
        </div>
      );
    }

    return (
      <div className={obtenerContenedorBotones()}>
        <IconButton
          title="cerrarBotom"
          aria-label="cerrar"
          onClick={cerrarModal}
          size="small"
          className={`${styles.sizeSmall} ${esDashboardTracking ? styles.botonCerrarImprevisto : CADENA_VACIA} ${
            detalleAvanceAbierto ? styles.botonCerrarImprevistoDetalle : CADENA_VACIA
          }`}
          data-testid={TEST_ID_CERRAR_MODAL_POLIGONO}
        >
          <img src={esDashboardTracking ? closePurple : cerrarModalLogo} alt="Cerrar" />
        </IconButton>
      </div>
    );
  };

  /**
   * Función que decide el título del modal (AC-002: imprevisto usa motivo cuando existe)
   * @returns Retorna titulo del modal
   */
  const tituloModal = () => {
    if (titulo === TIPOCORTE.PROXIMO_CORTE_PROGRAMADO) {
      return <Typography className={styles.textoTitulo}>{titulo}</Typography>;
    }

    if (titulo === TIPOCORTE.CORTE_IMPREVISTO && !datoNoValido(motivoCorte)) {
      return <Typography className={styles.textoTituloImprevisto}>{motivoCorte}</Typography>;
    }

    if (titulo === TIPOCORTE.CORTE_IMPREVISTO) {
      return (
        <Typography className={styles.textoTituloImprevisto}>
          {t('desconexion:paginas.mapaCortes.modalTracking.titulosModal.estadoDeTuServicio')}
        </Typography>
      );
    }

    return (
      <Typography className={styles.textoTitulo}>
        {t('desconexion:paginas.mapaCortes.modalTracking.corteEnCurso', {
          total: titulo,
        })}
      </Typography>
    );
  };
  return (
    <>
      {titulo !== CADENA_VACIA ? (
        <div className={obtenerClase()}>
          {contenedoresBotonesModal()}
          {titulo && (
            <>
              <div className={styles.cajaTitulo}>
                {estadoTracking !== CADENA_VACIA && <div className={tipoCorte(titulo)} />}
                {dataSinTracking === CADENA_VACIA && estadoTracking && !esDashboardTrackingMapa(titulo) && tituloModal()}
                {dataSinTracking && <Typography className={styles.textoTitulo}>{titulo}</Typography>}
              </div>
              {estadoTracking !== CADENA_VACIA && !esDashboardTrackingMapa(titulo) && <div className={styles.linea} />}
            </>
          )}

          <div className={esDashboardTracking ? styles.cuerpoImprevisto : styles.cuerpo} id={ID_MODAL_POLIGONO}>
            {estadoTracking && <div className={styles.tracking}>{estadoTracking}</div>}
            {children && <div className={esDashboardTracking ? styles.childrenImprevisto : styles.children}>{children}</div>}
            {dataSinTracking !== CADENA_VACIA && (
              <div className={styles.sinTracking}>
                {dataSinTracking.map((data: any) => (
                  <div className={styles.cajaTextoNoTracking}>
                    <Typography className={styles.textoTitulo}>{data.titulo}:</Typography>
                    <Typography className={styles.textoParrafo}>{data.texto}</Typography>
                  </div>
                ))}
              </div>
            )}
            {mostrarDataSinTracking()}
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
};

ModalPoligono.defaultProps = {
  titulo: CADENA_VACIA,
  setTitulo: CADENA_VACIA,
  estadoTracking: CADENA_VACIA,
  children: CADENA_VACIA,
  dataSinTracking: CADENA_VACIA,
  setDataSinTracking: CADENA_VACIA,
  setServicioCargado: CADENA_VACIA,
  setVentanaMobileState: CADENA_VACIA,
  activadorMobile: CADENA_VACIA,
  motivoCorte: CADENA_VACIA,
  detalleAvanceAbierto: false,
  compactoDesktop: false,
  sinGraficoDesktop: false,
};

export default ModalPoligono;
